<script setup lang="ts">
/**
 * One glyph per profile section, drawn rather than fetched.
 *
 * The same argument `StatIcon.vue`, the radar bezel, the title badges and the
 * key caps all make: this renders at ~15 CSS px, the game runs at
 * `devicePixelRatio`, and any fixed raster at fifteen device pixels is soft on
 * half the machines that play it. Stroked in `currentColor`, so a glyph takes
 * the colour of the rail chip it sits in — resting steel, `#fff` when that
 * section is the one on screen — and there is no second palette to keep in step.
 *
 * **It is on the rail and nowhere else.** A mark beside a block heading as well
 * would be the same fact said twice on one screen, which is the redundancy the
 * kill-feed rule argues against; and the rail is where a glyph does work a label
 * cannot, because below the width breakpoint the chips lose their blurb and the
 * silhouette is what is left to recognise a section by.
 *
 * The set is *silhouettes*, per `StatIcon`'s own rule — at this size outline is
 * the only channel that survives, so no two share a shape: a head and shoulders,
 * a sheet with a folded corner, an angled tag, an open book. Two documents that
 * differed only in what was printed on them would be one glyph drawn twice.
 *
 * **An unknown id falls back to a plain sheet rather than to nothing.** A rail
 * with a hole in it reads as a broken screen; a generic mark reads as a section
 * whose glyph has not been drawn yet, which is what it would be — the rule
 * `StatIcon` follows for an unrecognised stat and `DailyQuestBox` for an
 * unrecognised metric.
 */
import { computed } from 'vue';

/** Path data on a 16 × 16 box, stroked — never filled, so one stroke width
 *  reads the same on every glyph in the column. */
const GLYPHS: Record<string, string[]> = {
  // A head and shoulders. The only round-topped silhouette in the set.
  account: ['M10.4 5.6a2.4 2.4 0 1 1-4.8 0a2.4 2.4 0 1 1 4.8 0', 'M2.9 13.6c0-2.6 2.3-4.2 5.1-4.2s5.1 1.6 5.1 4.2'],
  // A sheet with its corner turned, ruled twice: an invoice.
  billing: ['M3.4 1.9h6L12.6 5v9.1H3.4z', 'M9.4 1.9V5h3.2', 'M5.6 8.4h4.8', 'M5.6 11h3.2'],
  // A price tag on the diagonal, pierced. Angled, so it cannot be read as a sheet.
  purchases: ['M8.3 1.9h5.8v5.8l-6.4 6.4-5.8-5.8z', 'M11.6 4.4v.01'],
  // An arrow leaving a box: money out.
  withdrawals: ['M3.4 3.4h9.2v9.2H3.4z', 'M8 6v7', 'M5.5 8.5 8 6l2.5 2.5'],
  // An open book: two pages over a spine. The one wide silhouette here.
  legal: ['M8 4.2v9.3', 'M8 4.2C6.6 3 4.9 2.5 2.4 2.5v8.6c2.5 0 4.2.5 5.6 1.7', 'M8 4.2c1.4-1.2 3.1-1.7 5.6-1.7v8.6c-2.5 0-4.2.5-5.6 1.7'],
};

/** The fallback: a plain sheet. Something is filed here; we have not drawn what. */
const FALLBACK = ['M3.6 1.9h8.8v12.2H3.6z', 'M6 5.4h4', 'M6 8.4h4', 'M6 11.4h2.6'];

const props = defineProps<{ name: string }>();

const glyph = computed(() => GLYPHS[props.name] ?? FALLBACK);
</script>

<template>
  <svg
    class="pf-icon"
    viewBox="0 0 16 16"
    aria-hidden="true"
    focusable="false"
    fill="none"
    stroke="currentColor"
    stroke-width="1.25"
    stroke-linecap="round"
    stroke-linejoin="round"
  >
    <path v-for="(d, i) in glyph" :key="i" :d="d" />
  </svg>
</template>

<style scoped>
.pf-icon {
  /* Sized in `em` so the chip's own font-size carries it, the rule
     `CurrencyIcon` follows — no caller states a pixel size, so the glyph
     cannot fall out of step with a label somebody retunes later. */
  flex: none;
  width: 1.25em;
  height: 1.25em;
}
</style>
